import React from 'react';
import Input from './Input';
import Button from './Button';
import FilterPopover from './FilterPopover';
import { Icon } from '../icons';
import { activeFilterChips } from './filterModel';
import { useT } from '../../i18n';

/**
 * FilterChip — a single active filter. Removable, and (when wrapped in a
 * FilterPopover) clickable to edit. The remove button stops propagation so it
 * doesn't also toggle the surrounding popover.
 */
export const FilterChip = ({ label, value, onRemove, editable = false }) => {
  const t = useT();
  return (
    <span className={`ps-chip${editable ? ' is-editable' : ''}`.trim()}>
      {label && <span className="ps-chip__key">{label}</span>}
      {value != null && <span className="ps-chip__value">{value}</span>}
      {onRemove && (
        <button
          type="button"
          className="ps-chip__remove"
          aria-label={label ? t('Remove {label}', { label }) : t('Remove filter')}
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
        >
          <Icon.X size={12} />
        </button>
      )}
    </span>
  );
};

/**
 * FilterBar — search + controls row, with active-filter chips below.
 *
 * Two ways to drive it:
 *
 * 1. Field-driven (recommended): pass `fields`, `values`, `onChange(id, value)`.
 *    The bar renders an "Add filter" popover and editable chips automatically.
 *
 * 2. Legacy: pass a pre-built `filters` array of { id, label, value, onRemove }.
 *
 * Other props:
 *   searchValue, onSearchChange, searchPlaceholder
 *   onClearAll: () => void
 *   actions: ReactNode            — right-aligned controls (e.g. buttons)
 *   children: ReactNode           — inline controls next to the search field
 */
const FilterBar = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  // field-driven mode
  fields,
  values,
  onChange,
  // legacy mode
  filters = [],
  onClearAll,
  actions,
  className = '',
  children,
}) => {
  const t = useT();
  const fieldDriven = Array.isArray(fields) && typeof onChange === 'function';
  const chips = fieldDriven ? activeFilterChips(fields, values) : filters;

  const removeField = (id) => {
    const field = fields.find((f) => f.id === id);
    if (field) onChange(id, undefined);
  };

  const clearAll = () => {
    if (fieldDriven) fields.forEach((f) => onChange(f.id, undefined));
    onClearAll?.();
  };

  return (
    <div className={`ps-filterbar ${className}`.trim()}>
      <div className="ps-filterbar__row">
        {onSearchChange !== undefined && (
          <div className="ps-filterbar__search">
            <Input
              size="sm"
              leadingIcon={<Icon.Search />}
              placeholder={searchPlaceholder ?? t('Search…')}
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
            />
          </div>
        )}
        {fieldDriven && (
          <FilterPopover
            fields={fields}
            values={values}
            onChange={onChange}
            onRemove={removeField}
            trigger={
              <Button size="sm" variant="outline" leadingIcon={<Icon.Filter />}>
                {t('Add filter')}
              </Button>
            }
          />
        )}
        {children && <div className="ps-filterbar__controls">{children}</div>}
        {actions && <div className="ps-filterbar__actions">{actions}</div>}
      </div>

      {chips.length > 0 && (
        <div className="ps-filterbar__chips">
          {chips.map((f) =>
            fieldDriven ? (
              <FilterPopover
                key={f.id}
                fields={fields}
                values={values}
                onChange={onChange}
                onRemove={removeField}
                initialFieldId={f.id}
                trigger={<FilterChip label={f.label} value={f.value} editable onRemove={() => removeField(f.id)} />}
              />
            ) : (
              <FilterChip key={f.id} label={f.label} value={f.value} onRemove={f.onRemove} />
            ),
          )}
          {(onClearAll || fieldDriven) && (
            <button type="button" className="ps-filterbar__clear" onClick={clearAll}>
              {t('Clear all')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
